import React from 'react'
import styled from 'styled-components'
import ScrollBackground from 'react-scrolling-color-background'

import { isClient } from '../../styles/utils'
import { pageBackgroundRgb } from '../../styles/colors'
import { homeGradient } from '../../styles/themes'

// react-scrolling-color-background works by reading the offsetTop of every
// [data-colour] element and interpolating between adjacent ones as you scroll.
// Previously only the homepage had varied stops (its WordPress sections each
// carry a colour_theme), so every other page sat on a flat background.
//
// Instead of depending on per-page CMS data, we lay the homepage's palette out
// as evenly spaced markers down the full document. Every page then scrolls
// through the same gradient.
const STOP_COUNT = homeGradient.length

// Document height is rounded before it becomes a remount key, so ordinary
// sub-pixel/scrollbar jitter doesn't thrash the child component.
const HEIGHT_QUANTUM = 100

class ScrollBg extends React.Component {

	state = {
		docHeight: 0
	}

	componentDidMount() {
		this.measure()

		// Images and web fonts land after mount and change the page height, so
		// re-measure once things have settled and again on any resize.
		this.timers = [
			setTimeout(this.measure, 250),
			setTimeout(this.measure, 1500),
		]
		window.addEventListener('resize', this.measure)
		window.addEventListener('load', this.measure)
	}

	componentDidUpdate(prevProps) {
		// Route change: the new page has a different height, so re-measure.
		if (prevProps.location.pathname !== this.props.location.pathname) {
			this.measure()
			this.timers.push(setTimeout(this.measure, 250))
		}
	}

	componentWillUnmount() {
		(this.timers || []).forEach(clearTimeout)
		window.removeEventListener('resize', this.measure)
		window.removeEventListener('load', this.measure)
	}

	measure = () => {
		if (!isClient()) return

		const height = Math.max(
			document.documentElement.scrollHeight,
			document.body ? document.body.scrollHeight : 0
		)

		const quantised = Math.round(height / HEIGHT_QUANTUM) * HEIGHT_QUANTUM

		if (quantised !== this.state.docHeight) {
			this.setState({ docHeight: quantised })
		}
	}

	renderStops = () => {
		const { docHeight } = this.state

		return (
			<Stops
				aria-hidden={'true'}
				style={{ height: `${docHeight}px` }}
			>
				{homeGradient.map((colour, i) => (
					<Stop
						key={i}
						data-colour={colour}
						style={{ top: `${(i / (STOP_COUNT - 1)) * 100}%` }}
					/>
				))}
			</Stops>
		)
	}

	render() {
		const { docHeight } = this.state

		// Nothing to interpolate against until the page has been measured.
		if (!isClient() || !docHeight) return null

		return (
			<React.Fragment>
				{/* Rendered first so it stays SiteWrapper's :first-child, which
				    is where the layout hangs the background-color transition.
				    The library selects stops by attribute, not sibling order,
				    so the markers below are still read in document order. */}
				<ScrollBackground
					// Remount whenever the stops move so their offsets are
					// re-read — the library only measures on mount.
					key={`${this.props.location.pathname}-${docHeight}`}
					selector={'[data-colour]'}
					colorDataAttribute={'data-colour'}
					initialRgb={pageBackgroundRgb}
				/>

				{this.renderStops()}
			</React.Fragment>
		)
	}
}

// Zero-width, non-interactive markers. They exist purely so the library has
// something to measure; nothing about them is visible.
const Stops = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 0;
	pointer-events: none;
	z-index: -1;
`

const Stop = styled.div`
	position: absolute;
	left: 0;
	width: 0;
	height: 0;
`

export default ScrollBg



// WEBPACK FOOTER //
// ./src/components/ScrollBackground/ScrollBackground.js